import { useAuth0 } from '@auth0/auth0-react';
import { useParties } from '@my/directory-api/hooks';
import type { InvitationStatusValue, Party, PartyStatusValue } from '@my/directory-api';
import { ErrorBanner, Loading, PageHeader } from '@my/ui';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Clock,
  Edit,
  Plus,
  Search,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { getCurrentOrganisationId } from '@/auth/currentOrg';

// ────────────────────────────────────────────────────────────────────────────
// PartyListPage — shared component, mounted twice under different routes
// (`/customers` and `/suppliers`). The `mode` prop drives:
//   • Page title + i18n namespace section.
//   • The `type` filter sent to the BE (`Customer` or `Supplier`). The BE
//     returns `Both` rows in either context, so a party flagged as both
//     shows on both pages without FE special-casing.
//   • The route base for navigation (Add → /customers/new vs /suppliers/new).
//
// Wave 1: read-only-ish grid with filter chips + search + delete. The
// PartyDetailsPage (edit + create) lands in wave 2 — the Add and Edit
// buttons here navigate to the corresponding routes, which currently
// render a placeholder until the editor is built.
// ────────────────────────────────────────────────────────────────────────────

/** Mode discriminator — the route mounts the page once per value. */
export type PartyListMode = 'Customer' | 'Supplier';

type SortKey = 'name' | 'code' | 'status';
type SortDir = 'asc' | 'desc';
type StatusFilter = PartyStatusValue | 'all';

const STATUS_DOT: Record<PartyStatusValue, string> = {
  Active: 'bg-green-500',
  Suspended: 'bg-yellow-400',
  CeasedTrading: 'bg-slate-400',
};

const STATUS_BADGE: Record<PartyStatusValue, string> = {
  Active: 'bg-green-100 text-green-700',
  Suspended: 'bg-yellow-100 text-yellow-800',
  CeasedTrading: 'bg-slate-200 text-slate-700',
};

/**
 * Pill styling for the Link column. Three-bucket palette mirroring the
 * legacy app:
 *   - Accepted → green "Linked" pill (the only "good" terminal state).
 *   - Pending  → amber "Pending" pill (awaiting the other side).
 *   - Not-yet-invited / terminal-bad → red pill ("Not invited", "Expired",
 *     "Revoked", "Rejected"). Distinct labels per status, same red palette
 *     so the eye groups them as "no link in place".
 */
interface LinkVariant {
  Icon: LucideIcon;
  classes: string;
  /** i18n key under `parties:table.linkBadge`. */
  i18nKey: 'linked' | 'pending' | 'notInvited' | 'expired' | 'revoked' | 'rejected';
}

const LINK_VARIANTS: Record<InvitationStatusValue, LinkVariant> = {
  Accepted: { Icon: CheckCircle2, classes: 'bg-green-100 text-green-700', i18nKey: 'linked' },
  Pending: { Icon: Clock, classes: 'bg-amber-100 text-amber-800', i18nKey: 'pending' },
  NotInvited: { Icon: XCircle, classes: 'bg-red-100 text-red-700', i18nKey: 'notInvited' },
  Expired: { Icon: XCircle, classes: 'bg-red-100 text-red-700', i18nKey: 'expired' },
  Revoked: { Icon: XCircle, classes: 'bg-red-100 text-red-700', i18nKey: 'revoked' },
  Rejected: { Icon: XCircle, classes: 'bg-red-100 text-red-700', i18nKey: 'rejected' },
};

interface PartyListPageProps {
  mode: PartyListMode;
}

export function PartyListPage({ mode }: PartyListPageProps) {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  // Multi-namespace: 'parties' for everything party-specific, 'common' for
  // shared verbs (`actions.add` / `actions.delete`). The mode drives which
  // sub-tree of the parties namespace we read for title / search / delete-
  // confirm copy.
  const { t } = useTranslation(['parties', 'common']);
  const navigate = useNavigate();

  const isCustomer = mode === 'Customer';
  const sectionT = isCustomer ? 'customers' : 'suppliers';
  const routeBase = isCustomer ? '/customers' : '/suppliers';

  // Pre-resolve labels — typed-keys i18n won't accept template literals,
  // so any dynamic lookup goes through these maps.
  const statusLabels: Record<PartyStatusValue, string> = {
    Active: t('parties:status.active'),
    Suspended: t('parties:status.suspended'),
    CeasedTrading: t('parties:status.ceasedTrading'),
  };

  const listQuery = useParties(orgId, mode);

  // No row-level mutations here — Delete moved to the details page where
  // operators have the full record in front of them. The list is purely
  // navigational.
  const isBusy = false;

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const startNew = () => navigate(`${routeBase}/new`);
  const startEdit = (party: Party) => navigate(`${routeBase}/${party.id}`);

  const hasActiveFilters = search.trim() !== '' || filterStatus !== 'all';
  const clearFilters = () => {
    setSearch('');
    setFilterStatus('all');
  };

  // ── Derived data ─────────────────────────────────────────────────────────

  const visibleParties = useMemo(() => {
    const all = listQuery.data ?? [];
    const q = search.trim().toLowerCase();
    const filtered = all.filter((p) => {
      if (q) {
        // Search across the visible-column fields plus tax number — gives
        // operators the broadest match for the kind of "where's that
        // record" lookups the list page exists for.
        const haystack = [
          p.name,
          p.code,
          p.taxNumber ?? '',
          p.networkId ?? '',
          p.primaryFullAddress ?? '',
          p.primaryAddressPostcode ?? '',
          p.primaryContactName ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (filterStatus !== 'all' && p.status !== filterStatus) return false;
      return true;
    });
    return applySort(filtered, sortKey, sortDir);
  }, [listQuery.data, search, filterStatus, sortKey, sortDir]);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader
        title={t(`parties:${sectionT}.title` as 'parties:customers.title')}
        description={t(`parties:${sectionT}.description` as 'parties:customers.description')}
      />

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
        {!orgId && (
          <ErrorBanner
            title="No organisation linked to this account"
            message="Your access token does not include a TMS organisation claim. Contact an administrator."
          />
        )}

        {orgId && listQuery.isPending && (
          <Loading label={t(`parties:${sectionT}.title` as 'parties:customers.title')} />
        )}

        {orgId && listQuery.isError && (
          <ErrorBanner title="Failed to load parties" message={listQuery.error.message} />
        )}

        {orgId && listQuery.isSuccess && (
          <>
            {/* ── Filters + search + add button ────────────────────────── */}
            <div className="flex flex-wrap items-center gap-2">
              {(['all', 'Active', 'Suspended', 'CeasedTrading'] as StatusFilter[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setFilterStatus(s)}
                  className={`inline-flex items-center gap-1 ${chipClass(filterStatus === s)}`}
                >
                  {s !== 'all' && (
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[s]}`}
                      aria-hidden="true"
                    />
                  )}
                  {s === 'all'
                    ? t('parties:filters.all')
                    : s === 'Active'
                      ? t('parties:filters.active')
                      : s === 'Suspended'
                        ? t('parties:filters.suspended')
                        : t('parties:filters.ceasedTrading')}
                </button>
              ))}

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-[10px] text-slate-400 underline hover:text-slate-600"
                >
                  {t('parties:filters.clear')}
                </button>
              )}

              <div className="flex-1" />

              {/* Search */}
              <div className="relative">
                <Search
                  className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  placeholder={t(`parties:${sectionT}.search` as 'parties:customers.search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-52 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* +Add — navigates to the create route. The verb comes from
                  common:actions.add so the same word reads identically
                  across every screen in the suite. */}
              <button
                type="button"
                onClick={startNew}
                disabled={isBusy}
                className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('common:actions.add')}
              </button>
            </div>

            {/* ── Table ─────────────────────────────────────────────────── */}
            {/* `rounded-md` lives on the scroll container so the rounded
                corners are preserved; the inner div used to carry
                `overflow-hidden rounded-md` together but the
                `overflow-hidden` was clipping any table content that
                grew wider than `min-w-[768px]` (long emails / address
                summaries push the natural width past that). With it
                clipped, the outer `overflow-x-auto` saw nothing past
                768px and the user couldn't scroll far enough on mobile
                to expose the Status column before the sticky-right
                Actions cell floated over it. */}
            <div className="overflow-x-auto rounded-md">
              <div className="min-w-[768px]">
                <table className="w-full table-auto text-xs">
                  <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                    <tr>
                      {/* Customer / Supplier — sortable by name. The
                          column is labelled per-mode so operators see
                          "Customer" on /customers and "Supplier" on
                          /suppliers, matching the legacy convention. */}
                      <SortableTh
                        label={t(isCustomer ? 'parties:table.customer' : 'parties:table.supplier')}
                        sortKey="name"
                        active={sortKey}
                        dir={sortDir}
                        onSort={handleSort}
                      />
                      <SortableTh
                        label={t('parties:table.code')}
                        sortKey="code"
                        active={sortKey}
                        dir={sortDir}
                        onSort={handleSort}
                      />
                      <Th>{t('parties:table.address')}</Th>
                      <Th>{t('parties:table.contact')}</Th>
                      <Th align="center">{t('parties:table.country')}</Th>
                      <Th>{t('parties:table.networkId')}</Th>
                      <Th align="center">{t('parties:table.currency')}</Th>
                      <Th align="center">{t('parties:table.linked')}</Th>
                      <SortableTh
                        label={t('parties:table.status')}
                        sortKey="status"
                        active={sortKey}
                        dir={sortDir}
                        onSort={handleSort}
                        align="center"
                      />
                      {/* Sticky-right Actions column — keeps the Edit
                          pencil reachable on mobile (table is wider
                          than the phone viewport, so the rightmost
                          column otherwise sits past max horizontal
                          scroll). The cell carries a solid slate-700
                          background so scrolling content can't bleed
                          through. */}
                      <th className="sticky right-0 z-10 whitespace-nowrap bg-slate-700 px-3 py-2 text-right font-semibold">
                        {t('parties:table.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-500 bg-white">
                    {visibleParties.length === 0 ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-3 py-6 text-center text-xs italic text-slate-400"
                        >
                          {hasActiveFilters
                            ? t('parties:table.emptyFiltered')
                            : isCustomer
                              ? t('parties:table.emptyCustomers', {
                                  add: t('common:actions.add'),
                                })
                              : t('parties:table.emptySuppliers', {
                                  add: t('common:actions.add'),
                                })}
                        </td>
                      </tr>
                    ) : (
                      visibleParties.map((p) => (
                        <PartyRow
                          key={p.id}
                          party={p}
                          statusLabel={statusLabels[p.status]}
                          isBusy={isBusy}
                          onEdit={() => startEdit(p)}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

// ── Components ──────────────────────────────────────────────────────────────

function Th({ children, align }: { children: React.ReactNode; align?: 'right' | 'center' }) {
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return <th className={`whitespace-nowrap px-3 py-2 font-semibold ${alignClass}`}>{children}</th>;
}

function SortableTh({
  label,
  sortKey,
  active,
  dir,
  onSort,
  align,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey | null;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  align?: 'right' | 'center';
}) {
  const isActive = active === sortKey;
  const Icon = isActive ? (dir === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th
      className={`group cursor-pointer select-none whitespace-nowrap px-3 py-2 font-semibold ${alignClass}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon
          className={`h-3 w-3 flex-shrink-0 transition-opacity ${
            isActive ? 'opacity-100' : 'opacity-40 group-hover:opacity-70'
          }`}
          aria-hidden="true"
        />
      </span>
    </th>
  );
}

interface PartyRowProps {
  party: Party;
  statusLabel: string;
  isBusy: boolean;
  onEdit: () => void;
}

function PartyRow({ party, statusLabel, isBusy, onEdit }: PartyRowProps) {
  const { t } = useTranslation('parties');

  // Address summary — the Google-formatted full line is the cleanest
  // single-string representation we can show in a grid cell. Falls
  // back to "postcode only" when fullAddress is missing on legacy
  // rows. (Pre-FullAddress this concatenated line1 + postcode.)
  const addressSummary = party.primaryFullAddress || party.primaryAddressPostcode || null;

  // Each invitation status maps to its own icon + colour + label —
  // matches the legacy three-bucket palette (green / amber / red). See
  // `LINK_VARIANTS` for the full table.
  const linkVariant = LINK_VARIANTS[party.invitationStatus];
  const LinkIcon = linkVariant.Icon;
  const linkLabel = t(`table.linkBadge.${linkVariant.i18nKey}`);

  return (
    // `group` lets the sticky Actions cell pick up the row hover
    // state via `group-hover:bg-slate-50` — without it the sticky
    // cell would stay solid white through hover and look visually
    // disconnected from the rest of the row.
    <tr
      className="group cursor-pointer hover:bg-slate-50"
      onDoubleClick={isBusy ? undefined : onEdit}
    >
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs font-semibold text-slate-900">
        {party.name}
      </td>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs font-semibold text-slate-700">
        {party.code}
      </td>
      <td className="px-3 py-1 align-top text-xs text-slate-500">
        {addressSummary ?? <span className="italic text-slate-300">—</span>}
      </td>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs text-slate-500">
        {party.primaryContactName || <span className="italic text-slate-300">—</span>}
      </td>
      <td className="px-3 py-1 text-center align-top text-xs text-slate-500">
        {party.primaryAddressCountryIsoCode || <span className="italic text-slate-300">—</span>}
      </td>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs">
        {party.networkId ? (
          // Render Network IDs in a monospaced pill so the fixed-format
          // identifier stands out from regular text columns.
          <span className="inline-flex items-center rounded-sm bg-pink-50 px-1.5 py-0.5 font-mono text-[10px] text-pink-700">
            {party.networkId}
          </span>
        ) : (
          <span className="italic text-slate-300">—</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1 text-center align-top text-xs text-slate-500">
        <span className="inline-block rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
          {party.currencyIsoCode}
        </span>
      </td>
      <td className="px-3 py-1 text-center align-top">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${linkVariant.classes}`}
        >
          <LinkIcon className="h-3 w-3" aria-hidden="true" />
          {linkLabel}
        </span>
      </td>
      <td className="px-3 py-1 text-center align-top">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_BADGE[party.status]}`}
        >
          {statusLabel}
        </span>
      </td>
      {/* Sticky-right pin — see the matching `<th>` comment above. */}
      <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-3 py-1 text-right align-top group-hover:bg-slate-50">
        <button
          type="button"
          aria-label={`Edit ${party.name}`}
          disabled={isBusy}
          onClick={(e) => {
            e.stopPropagation();
            if (!isBusy) onEdit();
          }}
          className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
        >
          <Edit className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function applySort(list: Party[], key: SortKey | null, dir: SortDir): Party[] {
  if (!key) {
    // Default: name ascending. Matches what operators expect on first paint.
    return [...list].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );
  }
  const mul = dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    switch (key) {
      case 'name':
        return mul * a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      case 'code':
        return mul * a.code.localeCompare(b.code, undefined, { sensitivity: 'base' });
      case 'status':
        return mul * a.status.localeCompare(b.status);
    }
  });
}

function chipClass(active: boolean): string {
  return `rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors ${
    active
      ? 'border-slate-700 bg-slate-700 text-white'
      : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700'
  }`;
}
